import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch, apiUpload, apiPut, apiDelete, apiDownload } from '../api/client';
import type {
  HeirloomListResponse,
  HeirloomResponse,
  UpdateHeirloomRequest,
} from '../types/api';
import { VaultRole } from '../types/api';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import DropZone from './DropZone';
import SortableList from './SortableList';
import './HeirloomsList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

export default function HeirloomsList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [heirlooms, setHeirlooms] = useState<HeirloomResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});

  // Create form state
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [dragOverPhoto, setDragOverPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editInstructions, setEditInstructions] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState<HeirloomResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Photo upload state
  const photoRef = useRef<HTMLInputElement>(null);
  const [uploadingPhotoId, setUploadingPhotoId] = useState<string | null>(null);

  const canEdit = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  const loadHeirlooms = useCallback(async () => {
    try {
      const data = await apiGet<HeirloomListResponse>(`/api/vault/${vaultId}/heirlooms`);
      setHeirlooms(data.heirlooms);
      onCountChangeRef.current?.(data.heirlooms.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load possession record.');
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => { loadHeirlooms(); }, [loadHeirlooms]);

  async function handleReorder(reordered: HeirloomResponse[]) {
    setHeirlooms(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/heirlooms/reorder`, {
        orderedIds: reordered.map(h => h.id),
      });
    } catch {
      await loadHeirlooms();
    }
  }

  // Load thumbnails for heirlooms with photos
  useEffect(() => {
    heirlooms
      .filter(h => h.hasPhoto && !thumbUrls[h.id])
      .forEach(async (h) => {
        try {
          const { blob } = await apiDownload(
            `/api/vault/${vaultId}/heirlooms/${h.id}/photo`
          );
          const url = URL.createObjectURL(blob);
          setThumbUrls(prev => ({ ...prev, [h.id]: url }));
        } catch {
          // Thumbnail load failed silently
        }
      });
  }, [heirlooms, vaultId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId(null);
      setEditingId(null);
      return;
    }
    setExpandedId(id);
    setEditingId(null);
  }

  function startEdit(h: HeirloomResponse) {
    setEditingId(h.id);
    setEditTitle(h.title);
    setEditInstructions(h.instructions);
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function handleSaveEdit(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError('');
    setEditSaving(true);

    try {
      const body: UpdateHeirloomRequest = {
        title: editTitle,
        instructions: editInstructions,
      };
      await apiPut(`/api/vault/${vaultId}/heirlooms/${editingId}`, body);
      setEditingId(null);
      setExpandedId(null);
      await loadHeirlooms();
    } catch (err: any) {
      setError(err.message || 'Failed to update item.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('instructions', instructions);

      const file = droppedFile || fileRef.current?.files?.[0];
      if (file) {
        formData.append('photo', file);
      }

      await apiUpload(`/api/vault/${vaultId}/heirlooms`, formData);
      setTitle('');
      setInstructions('');
      setDroppedFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setShowForm(false);
      await loadHeirlooms();
    } catch (err: any) {
      setError(err.message || 'Failed to save item.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/heirlooms/${confirmDelete.id}`);
      if (thumbUrls[confirmDelete.id]) {
        URL.revokeObjectURL(thumbUrls[confirmDelete.id]);
        setThumbUrls(prev => {
          const next = { ...prev };
          delete next[confirmDelete.id];
          return next;
        });
      }
      setConfirmDelete(null);
      if (expandedId === confirmDelete.id) {
        setExpandedId(null);
        setEditingId(null);
      }
      await loadHeirlooms();
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  function handleFileDrop(files: FileList) {
    setDroppedFile(files[0]);
    setShowForm(true);
  }

  async function handlePhotoUpload(heirloomId: string, file: File) {
    setError('');
    setUploadingPhotoId(heirloomId);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      await apiUpload(`/api/vault/${vaultId}/heirlooms/${heirloomId}/photo`, formData);
      // Revoke old thumbnail
      if (thumbUrls[heirloomId]) {
        URL.revokeObjectURL(thumbUrls[heirloomId]);
        setThumbUrls(prev => {
          const next = { ...prev };
          delete next[heirloomId];
          return next;
        });
      }
      await loadHeirlooms();
    } catch (err: any) {
      setError(err.message || 'Photo upload failed.');
    } finally {
      setUploadingPhotoId(null);
    }
  }

  function triggerPhotoUpload(heirloomId: string) {
    const input = photoRef.current;
    if (!input) return;
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) handlePhotoUpload(heirloomId, file);
      input.value = '';
    };
    input.click();
  }

  if (loading) return <Skeleton variant="row" count={3} />;

  return (
    <div>
      <div className="heirlooms-header">
        <h3>Possessions</h3>
        <div className="heirlooms-header-actions">
          {heirlooms.length > 0 && (
            <Link to={`/vault/${vaultId}/print/heirlooms`} className="heirlooms-print-link">
              Print List
            </Link>
          )}
          <button
            className="btn-text"
            onClick={() => canEdit && setShowForm(!showForm)}
            disabled={!canEdit}
          >
            {showForm ? 'Cancel' : '+ Add Item'}
          </button>
        </div>
      </div>

      <p className="heirlooms-description">
        Catalog your treasured possessions &mdash; photos, descriptions, and instructions
        for who should receive each item.
      </p>

      <AlertMessage type="error" message={error} />

      {/* Hidden file input for photo replacement */}
      <input
        type="file"
        accept="image/*"
        ref={photoRef}
        style={{ display: 'none' }}
      />

      {showForm && (
        <form className="heirloom-form" onSubmit={handleCreate}>
          <div className="form-group">
            <label className="form-label" htmlFor="heirloomTitle">Title</label>
            <input
              id="heirloomTitle"
              className="form-input"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={'e.g., "Grandmother\'s Diamond Ring"'}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="heirloomInstructions">
              Who receives this &amp; instructions
            </label>
            <textarea
              id="heirloomInstructions"
              className="form-input"
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              placeholder="Describe who should receive this item and any special instructions for its care..."
              required
              maxLength={5000}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Photo (optional)</label>
            {/* Hidden input — opened programmatically on click */}
            <input
              type="file"
              accept="image/*"
              ref={fileRef}
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) setDroppedFile(file);
                e.target.value = '';
              }}
            />
            {droppedFile ? (
              <div className="heirloom-dropped-file">
                <span className="heirloom-dropped-name">{droppedFile.name}</span>
                <button
                  type="button"
                  className="btn-text heirloom-dropped-clear"
                  onClick={() => setDroppedFile(null)}
                >
                  Change
                </button>
              </div>
            ) : (
              <div
                className={`heirloom-photo-dropzone${dragOverPhoto ? ' heirloom-photo-dropzone--active' : ''}`}
                onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragOverPhoto(true); }}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragOverPhoto(false); }}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverPhoto(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file && file.type.startsWith('image/')) setDroppedFile(file);
                }}
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && fileRef.current?.click()}
              >
                <span className="heirloom-photo-dropzone-icon">🖼</span>
                <span className="heirloom-photo-dropzone-text">
                  Drop an image here or <u>browse</u>
                </span>
              </div>
            )}
          </div>
          <div className="heirloom-form-actions">
            <button className="btn-secondary" type="button" onClick={() => {
              setShowForm(false);
              setDroppedFile(null);
            }}>
              Cancel
            </button>
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? <LoadingSpinner small /> : 'Save Item'}
            </button>
          </div>
        </form>
      )}

      <DropZone onFileDrop={handleFileDrop} accept="image/*" enabled={canEdit} label="Drop photo to add item">
        {heirlooms.length === 0 ? (
          <EmptyState
            icon="heirlooms"
            title="No items yet"
            subtitle={canEdit ? "Drag a photo here or click '+ Add Item' above." : undefined}
          />
        ) : (
          <div className="heirlooms-list">
            <SortableList
              items={heirlooms}
              canReorder={canEdit}
              onReorder={handleReorder}
              renderItem={(h) => (
              <div className={`heirloom-item${expandedId === h.id ? ' heirloom-item--expanded' : ''}`}>
                <div className="heirloom-item-header" onClick={() => handleExpand(h.id)}>
                  {thumbUrls[h.id] ? (
                    <img
                      className="heirloom-thumb"
                      src={thumbUrls[h.id]}
                      alt={h.title}
                    />
                  ) : (
                    <div className="heirloom-thumb heirloom-thumb--placeholder">
                      <span className="heirloom-thumb-icon">&#128142;</span>
                    </div>
                  )}
                  <div className="heirloom-item-info">
                    <div className="heirloom-item-title">{h.title}</div>
                    <div className="heirloom-item-preview">
                      {h.instructions.length > 80
                        ? h.instructions.substring(0, 80) + '...'
                        : h.instructions}
                    </div>
                  </div>
                  <span className="heirloom-item-chevron">{expandedId === h.id ? '\u25B2' : '\u25BC'}</span>
                </div>

                {expandedId === h.id && (
                  <div className="heirloom-item-body">
                    {editingId === h.id ? (
                      <form className="heirloom-edit-form" onSubmit={handleSaveEdit}>
                        <div className="form-group">
                          <label className="form-label" htmlFor="editHeirloomTitle">Title</label>
                          <input
                            id="editHeirloomTitle"
                            className="form-input"
                            type="text"
                            value={editTitle}
                            onChange={e => setEditTitle(e.target.value)}
                            required
                            maxLength={200}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="editHeirloomInstructions">
                            Who receives this &amp; instructions
                          </label>
                          <textarea
                            id="editHeirloomInstructions"
                            className="form-input"
                            value={editInstructions}
                            onChange={e => setEditInstructions(e.target.value)}
                            required
                            maxLength={5000}
                          />
                        </div>
                        <div className="heirloom-form-actions">
                          <button className="btn-secondary" type="button" onClick={cancelEdit} disabled={editSaving}>
                            Cancel
                          </button>
                          <button className="btn-primary" type="submit" disabled={editSaving}>
                            {editSaving ? <LoadingSpinner small /> : 'Save Changes'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        {thumbUrls[h.id] && (
                          <div className="heirloom-item-photo-full">
                            <img src={thumbUrls[h.id]} alt={h.title} />
                          </div>
                        )}
                        <div className="heirloom-item-instructions">
                          <div className="heirloom-item-instructions-label">
                            Who receives this &amp; instructions
                          </div>
                          {h.instructions}
                        </div>
                        <div className="heirloom-item-actions">
                          <button className="heirloom-edit-btn" onClick={() => startEdit(h)} disabled={!canEdit}>
                            Edit
                          </button>
                          <button
                            className="heirloom-photo-btn"
                            onClick={() => triggerPhotoUpload(h.id)}
                            disabled={!canEdit || uploadingPhotoId === h.id}
                          >
                            {uploadingPhotoId === h.id ? 'Uploading...' : h.hasPhoto ? 'Replace Photo' : 'Add Photo'}
                          </button>
                          <button className="heirloom-delete-btn" onClick={() => setConfirmDelete(h)} disabled={!canEdit}>
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              )}
            />
          </div>
        )}
      </DropZone>

      {confirmDelete && (
        <div className="heirloom-confirm-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="heirloom-confirm-dialog" onClick={e => e.stopPropagation()}>
            <h4>Delete Item</h4>
            <p>
              Are you sure you want to delete <strong>{confirmDelete.title}</strong>?
              This action cannot be undone.
            </p>
            <div className="heirloom-confirm-actions">
              <button
                className="heirloom-confirm-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                className="heirloom-confirm-cancel"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
